import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FadeIn } from "@/components/Reveal";
import { SITE } from "@/config/site";

const SERVICE_OPTIONS = [
  "Compliance Support",
  "SOP Development",
  "I-9 Verification",
  "Background Screening",
  "Notary Services",
  "Loan Signing",
  "Apostille Support",
  "Field Inspection",
  "Other Business Service",
];

const INITIAL = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  service_needed: "",
  preferred_service_date: "",
  service_location: "",
  preferred_contact: "",
  mobile_service_needed: "No",
  description: "",
  consent: false,
};

export const ServiceRequestForm = ({ defaultService = "" }) => {
  const [form, setForm] = useState({ ...INITIAL, service_needed: defaultService });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setErrorMsg("Please check the consent box so TSM Group CLE can contact you about your request.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, mobile_service_needed: form.mobile_service_needed === "Yes" }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch (err) {
      setErrorMsg("Something went wrong while submitting your request. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section id="request" className="bg-paper py-24 lg:py-32">
      <div className="container-tsm grid gap-14 lg:grid-cols-12 lg:gap-10">
        <FadeIn className="lg:col-span-5">
          <p className="eyebrow">Request a Service</p>
          <h2
            className="mt-6 font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl"
            data-testid="request-headline"
          >
            Tell us what you need. <span className="italic text-gold">We&rsquo;ll take it from there.</span>
          </h2>
          <p className="mt-8 max-w-md leading-relaxed text-ink/70" data-testid="request-intro">
            Complete the form below and our team will review your request, confirm service details, and coordinate
            scheduling and next steps.
          </p>
          <div className="mt-8 rounded-lg border border-teal/25 bg-white p-5" data-testid="request-what-next">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-tealdeep">What happens next</p>
            <ol className="mt-3 space-y-2 text-sm leading-relaxed text-ink/70">
              <li className="flex gap-2.5"><span className="font-bold text-tealdeep">1.</span> We review your request and the service details you provided.</li>
              <li className="flex gap-2.5"><span className="font-bold text-tealdeep">2.</span> A member of our team contacts you using your preferred contact method.</li>
              <li className="flex gap-2.5"><span className="font-bold text-tealdeep">3.</span> We confirm scope, timing, and any documents needed before work begins.</li>
            </ol>
          </div>
          <div className="mt-10 space-y-3 border-t border-navy/10 pt-8 text-sm text-ink/70">
            <p>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/50">Phone</span>
              <br />
              <span className="text-base text-navy" data-testid="request-side-phone">{SITE.phone}</span>
            </p>
            <p>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/50">Email</span>
              <br />
              <span className="text-base text-navy" data-testid="request-side-email">{SITE.email}</span>
            </p>
          </div>
          <p className="mt-8 text-xs italic leading-relaxed text-ink/50">
            For business risk management and operational discussions, you may also schedule a consultation with our team.
          </p>
        </FadeIn>

        <FadeIn delay={0.15} className="lg:col-span-7">
          <div className="rounded-lg border border-navy/10 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.05)] sm:p-9 lg:p-11">
            {status === "success" ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="request-success">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                  <CheckCircle2 className="h-8 w-8 text-gold" />
                </span>
                <h3 className="mt-7 font-serif text-3xl font-semibold text-navy">Request received.</h3>
                <p className="mt-4 max-w-md leading-relaxed text-ink/70">
                  Thank you for contacting TSM Group CLE. Your request has been received. A member of our team will
                  review the information and contact you regarding next steps.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate={false} data-testid="service-request-form">
                <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="full_name" className="field-label">Full Name *</Label>
                    <Input id="full_name" required value={form.full_name} onChange={update("full_name")} className="field-input" placeholder="Jane Doe" data-testid="request-input-full-name" />
                  </div>
                  <div>
                    <Label htmlFor="business_name" className="field-label">Business Name</Label>
                    <Input id="business_name" value={form.business_name} onChange={update("business_name")} className="field-input" placeholder="Acme Consulting LLC" data-testid="request-input-business-name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="field-label">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={update("email")} className="field-input" placeholder="name@company.com" data-testid="request-input-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="field-label">Phone *</Label>
                    <Input id="phone" type="tel" required value={form.phone} onChange={update("phone")} className="field-input" placeholder="(216) 555-0100" data-testid="request-input-phone" />
                  </div>
                  <div>
                    <Label className="field-label">Service Needed *</Label>
                    <Select value={form.service_needed} onValueChange={(v) => setForm((f) => ({ ...f, service_needed: v }))}>
                      <SelectTrigger className="field-input !h-auto gap-2" data-testid="request-select-service" aria-label="Service needed">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt} data-testid={`request-service-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferred_service_date" className="field-label">Preferred Service Date</Label>
                    <Input id="preferred_service_date" type="date" value={form.preferred_service_date} onChange={update("preferred_service_date")} className="field-input" data-testid="request-input-date" />
                  </div>
                  <div>
                    <Label htmlFor="service_location" className="field-label">Service Location</Label>
                    <Input id="service_location" value={form.service_location} onChange={update("service_location")} className="field-input" placeholder="City, address, or virtual" data-testid="request-input-location" />
                  </div>
                  <div>
                    <Label className="field-label">Preferred Contact Method</Label>
                    <Select value={form.preferred_contact} onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}>
                      <SelectTrigger className="field-input !h-auto gap-2" data-testid="request-select-contact-method" aria-label="Preferred contact method">
                        <SelectValue placeholder="Select a contact method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Phone call">Phone call</SelectItem>
                        <SelectItem value="Text message">Text message</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="field-label">Mobile Service Needed</Label>
                    <Select value={form.mobile_service_needed} onValueChange={(v) => setForm((f) => ({ ...f, mobile_service_needed: v }))}>
                      <SelectTrigger className="field-input !h-auto gap-2" data-testid="request-select-mobile" aria-label="Mobile service needed">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="description" className="field-label">Brief Description of Request</Label>
                    <Textarea id="description" value={form.description} onChange={update("description")} className="field-input min-h-24" placeholder="Tell us briefly what you need help with" data-testid="request-input-description" />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-start gap-3 rounded-sm border border-navy/10 bg-paper p-4">
                      <Checkbox
                        id="consent"
                        checked={form.consent}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                        className="mt-0.5 border-navy/30 data-[state=checked]:border-gold data-[state=checked]:bg-gold data-[state=checked]:text-navy"
                        data-testid="request-input-consent"
                      />
                      <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-ink/75">
                        I agree to allow TSM Group CLE to contact me regarding this service request. *
                      </Label>
                    </div>
                  </div>
                  {status === "error" && errorMsg && (
                    <p className="sm:col-span-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="request-error">
                      {errorMsg}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="request-submit-button"
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          Submit Service Request
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
